// TanStack Query wrappers for /v1/api-keys.
//
// Same philosophy as useKnowledge: list staleTime 30s, mutations invalidate
// the list. No polling — keys don't change out-of-band.

import {
  type ApiKeyCreateOut,
  type ApiKeyListResponse,
  type CreateApiKeyBody,
  createApiKey,
  listApiKeys,
  revokeApiKey,
} from '@/lib/api-keys';
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const API_KEYS_QUERY_KEY = ['api-keys'] as const;

export function useApiKeysList() {
  return useQuery<ApiKeyListResponse, AxiosError>({
    queryKey: API_KEYS_QUERY_KEY,
    queryFn: () => listApiKeys(),
    staleTime: 30_000,
  });
}

export function useCreateApiKey(
  options?: Omit<UseMutationOptions<ApiKeyCreateOut, AxiosError, CreateApiKeyBody>, 'mutationFn'>,
) {
  const qc = useQueryClient();
  return useMutation<ApiKeyCreateOut, AxiosError, CreateApiKeyBody>({
    mutationFn: (body) => createApiKey(body),
    ...options,
    onSuccess: (key, ...rest) => {
      qc.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
      options?.onSuccess?.(key, ...rest);
    },
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => revokeApiKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
  });
}
